import { getPromoPopupImageUrl } from './siteBranding';
import {
  getCreatorIdentifierFromShopPath,
  getSingleSegmentFromPath,
  isLegacyCreatorPath,
  isMongoObjectIdString,
  STOREFRONT_SEGMENT_MODE_EVENT,
} from './creatorSlug';

export const CREATOR_PROMO_LS_PREFIX = 'sheenwear_creator_promo_';

export const DEFAULT_SITE_PROMO_PAGE_FLAGS = {
  promoPopupShowOnHome: true,
  promoPopupShowOnProductPage: true,
  promoPopupShowOnShopkeeperPage: false,
};

export const EMPTY_PROMO_CONFIG = {
  promoPopupEnabled: false,
  promoPopupImageUrl: '',
  promoPopupDelaySeconds: 3,
  promoPopupAutoCloseSeconds: 8,
};

/** @typedef {'home' | 'product' | 'creator' | null} PromoPageType */

/** @returns {PromoPageType} */
export function getPromoPageTypeFromPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/home') return 'home';
  if (/^\/product\/[^/]+$/i.test(path)) return 'product';
  if (isLegacyCreatorPath(path)) return 'creator';
  const seg = getSingleSegmentFromPath(path);
  if (seg && isMongoObjectIdString(seg)) return 'creator';
  return null;
}

/** Legacy `/creator/x`, `/creators/x`, or root shop handle when known. */
export function extractCreatorIdFromPath(pathname) {
  return getCreatorIdentifierFromShopPath(pathname);
}

export { STOREFRONT_SEGMENT_MODE_EVENT };

function truthyFlag(raw, defaultWhenMissing = true) {
  if (raw === undefined || raw === null) return defaultWhenMissing;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw !== 0;
  if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase();
    if (['false', '0', 'no', 'off'].includes(t)) return false;
    if (['true', '1', 'yes', 'on'].includes(t)) return true;
  }
  return defaultWhenMissing;
}

export function normalizePromoConfig(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_PROMO_CONFIG };
  }
  const url = getPromoPopupImageUrl(raw);
  return {
    promoPopupEnabled: truthyFlag(raw.promoPopupEnabled, !!url),
    promoPopupImageUrl: url,
    promoPopupDelaySeconds: clampInt(raw.promoPopupDelaySeconds, 0, 600, 3),
    promoPopupAutoCloseSeconds: clampInt(raw.promoPopupAutoCloseSeconds, 0, 300, 8),
  };
}

export function normalizeSitePromoPageFlags(siteSettings) {
  const s = siteSettings && typeof siteSettings === 'object' ? siteSettings : {};
  return {
    promoPopupShowOnHome: truthyFlag(
      s.promoPopupShowOnHome,
      DEFAULT_SITE_PROMO_PAGE_FLAGS.promoPopupShowOnHome
    ),
    promoPopupShowOnProductPage: truthyFlag(
      s.promoPopupShowOnProductPage,
      DEFAULT_SITE_PROMO_PAGE_FLAGS.promoPopupShowOnProductPage
    ),
    promoPopupShowOnShopkeeperPage: truthyFlag(
      s.promoPopupShowOnShopkeeperPage,
      DEFAULT_SITE_PROMO_PAGE_FLAGS.promoPopupShowOnShopkeeperPage
    ),
  };
}

export function isGlobalPromoAllowedOnPage(siteSettings, pageType) {
  if (!pageType) return false;
  const flags = normalizeSitePromoPageFlags(siteSettings);
  if (pageType === 'home') return flags.promoPopupShowOnHome;
  if (pageType === 'product') return flags.promoPopupShowOnProductPage;
  if (pageType === 'creator') return flags.promoPopupShowOnShopkeeperPage;
  return false;
}

export function isPromoConfigActive(config) {
  const c = normalizePromoConfig(config);
  if (!c.promoPopupImageUrl) return false;
  if (c.promoPopupEnabled === false) return false;
  return true;
}

export function getCreatorPromoStorageKey(creatorId) {
  return `${CREATOR_PROMO_LS_PREFIX}${String(creatorId || '').trim()}`;
}

export function readCreatorPromoFromStorage(creatorId) {
  if (typeof window === 'undefined' || !creatorId) return null;
  try {
    const raw = localStorage.getItem(getCreatorPromoStorageKey(creatorId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? normalizePromoConfig(parsed) : null;
  } catch {
    return null;
  }
}

export function writeCreatorPromoToStorage(creatorId, config) {
  if (typeof window === 'undefined' || !creatorId) return;
  try {
    localStorage.setItem(getCreatorPromoStorageKey(creatorId), JSON.stringify(normalizePromoConfig(config)));
  } catch {
    /* ignore */
  }
}

/** Map public creator profile / admin profile fields → promo config */
export function promoConfigFromCreatorProfile(profile) {
  if (!profile || typeof profile !== 'object') return null;
  const nested = profile.promoPopup && typeof profile.promoPopup === 'object' ? profile.promoPopup : profile;
  const merged = {
    promoPopupEnabled:
      nested.promoPopupEnabled ??
      nested.enabled ??
      profile.promoPopupEnabled ??
      profile.promoEnabled,
    promoPopupImageUrl:
      nested.promoPopupImageUrl ??
      nested.imageUrl ??
      profile.promoPopupImageUrl ??
      nested.creatorPromoImageUrl ??
      profile.creatorPromoImageUrl,
    promoPopupDelaySeconds:
      nested.promoPopupDelaySeconds ??
      nested.delaySeconds ??
      profile.promoPopupDelaySeconds ??
      profile.creatorPromoDelaySeconds,
    promoPopupAutoCloseSeconds:
      nested.promoPopupAutoCloseSeconds ??
      nested.autoCloseSeconds ??
      profile.promoPopupAutoCloseSeconds ??
      profile.creatorPromoAutoCloseSeconds,
  };
  if (!getPromoPopupImageUrl(merged) && merged.promoPopupEnabled === undefined) return null;
  return normalizePromoConfig(merged);
}

/** Prefer API values when set; fall back to local storage so admin UI always shows saved image. */
export function mergeCreatorPromoSources(stored, apiProfile) {
  const fromApi = promoConfigFromCreatorProfile(apiProfile);
  const fromStore = stored ? normalizePromoConfig(stored) : null;
  if (!fromApi && !fromStore) return null;

  const apiUrl = getPromoPopupImageUrl(fromApi);
  const storeUrl = getPromoPopupImageUrl(fromStore);

  return normalizePromoConfig({
    promoPopupEnabled:
      fromApi?.promoPopupEnabled !== undefined
        ? fromApi.promoPopupEnabled
        : fromStore?.promoPopupEnabled,
    promoPopupImageUrl: apiUrl || storeUrl,
    promoPopupDelaySeconds:
      fromApi?.promoPopupDelaySeconds ?? fromStore?.promoPopupDelaySeconds,
    promoPopupAutoCloseSeconds:
      fromApi?.promoPopupAutoCloseSeconds ?? fromStore?.promoPopupAutoCloseSeconds,
  });
}

/** Session fingerprint key — scope separates site vs each shopkeeper */
export function promoStorageFingerprintKey(config, scope) {
  const url = getPromoPopupImageUrl(config);
  const d = String(config?.promoPopupDelaySeconds ?? '');
  const a = String(config?.promoPopupAutoCloseSeconds ?? '');
  let h = 2166136261;
  const str = `${scope}|${url}|${d}|${a}`;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `sheenwear_promo_fp_${scope}_${(h >>> 0).toString(36)}`;
}

function clampInt(value, min, max, fallback) {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export function clampPromoDelay(value) {
  return clampInt(value, 0, 600, 3);
}

export function clampPromoAutoClose(value) {
  return clampInt(value, 0, 300, 8);
}
